import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { userService } from "@/services/breeze/UserService";
import InputField from "@/components/form/InputField";
import { SkeletonPage } from "@/components/ui/Skeleton";
import Tabs from "@/components/ui/Tabs";
import { toast } from "react-hot-toast";
import { UpdateMyUserRequest } from "@/types/breeze/User";
import ApiKeyManager from "@/components/api-key-manager/ApiKeyManager";
import PageTemplate from "@/components/page-template/PageTemplate";
import { Button, Text } from "@radix-ui/themes";

export default function MyAccountPage() {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    queryKey: ["user", "me"],
    queryFn: async () => {
      return userService.getMyUser();
    },
  });

  const { mutate: updateUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: (data: UpdateMyUserRequest) => userService.updateMyUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      toast.success("Profile updated successfully");
    },
  });

  if (isUserLoading) return <SkeletonPage title="My Account" />;
  if (userError || !user) return <div>Error loading account settings</div>;

  return (
    <PageTemplate title="My Account">
      <Tabs
        items={[
          {
            key: "profile",
            label: "Profile",
            content: (
              <div>
                <Text size="4" className="font-bold block mb-2">
                  Profile
                </Text>
                <Formik
                  initialValues={{
                    first_name: user.first_name,
                    last_name: user.last_name,
                  }}
                  onSubmit={(values) => {
                    updateUser(values);
                  }}
                >
                  <Form className="space-y-4">
                    <InputField name="first_name" label="First Name" required />
                    <InputField name="last_name" label="Last Name" required />
                    <div>
                      <Text
                        as="label"
                        size="2"
                        weight="medium"
                        mb="1"
                        className="block"
                      >
                        Email
                      </Text>
                      <Text className="mt-1">{user.email}</Text>
                    </div>
                    <Button type="submit" loading={isUpdatingUser}>
                      Update Profile
                    </Button>
                  </Form>
                </Formik>
              </div>
            ),
          },
          {
            key: "api-keys",
            label: "API Keys",
            content: <ApiKeyManager />,
          },
        ]}
      />
    </PageTemplate>
  );
}
